
"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { 
  UserIcon, 
  AtSymbolIcon, 
  EyeIcon, 
  EyeSlashIcon,
  ExclamationCircleIcon 
} from "@heroicons/react/24/outline";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuthentication } from "@/hooks/useAuthentication";
import { apiClient } from "@/lib/api";
import { toast } from "sonner"; 
import { useLocale } from "@/context/LocaleContext";

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  terms?: string;
}

export default function RegisterClient() { 
  const { t, locale } = useLocale();
  usePageTitle(t("Register.register_btn") || "Register");
  const router = useRouter();
  const searchParams = useSearchParams();
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const { registerUser, loginWithGoogle, isLoading, error: serverError } = useAuthentication();

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    if (!name.trim()) {
      errors.name = t("Register.error_name_required") || "Masukkan Nama terlebih dahulu...";
      isValid = false;
    } else if (name.trim().length < 2) {
      errors.name = t("Register.error_name_min") || "Nama minimal 2 karakter";
      isValid = false;
    } else if (!/^[a-zA-Z\s]*$/.test(name)) {
      errors.name = t("Register.error_name_invalid") || "Nama hanya boleh mengandung huruf dan spasi";
      isValid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      errors.email = t("Register.error_email_required") || "Masukkan email terlebih dahulu...";
      isValid = false;
    } else if (!emailRegex.test(email)) {
      errors.email = t("Register.error_email_invalid") || "Masukkan email yang valid";
      isValid = false;
    }

    if (!password) {
      errors.password = t("Register.error_password_required") || "Masukkan password terlebih dahulu...";
      isValid = false;
    } else if (password.length < 8) {
      errors.password = t("Register.error_password_min") || "Password minimal 8 karakter";
      isValid = false;
    } 

    if (!confirmPassword) {
      errors.confirmPassword = t("Register.error_confirm_required") || "Harap konfirmasi password Anda...";
      isValid = false;
    } else if (password !== confirmPassword) {
      errors.confirmPassword = t("Register.error_password_mismatch") || "Password tidak sama";
      isValid = false;
    }

    if (!agreeTerms) {
      errors.terms = t("Register.error_terms_required") || "Wajib menyetujui syarat dan ketentuan terlebih dahulu...";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;
    
    const cleanEmail = email.trim();
    const cleanName = name.trim();

    const result = await registerUser({
      name: cleanName,
      email: cleanEmail,
      password: password,
      confirm_password: confirmPassword,
    });

    if (result.success) {
      try {
        await apiClient.post(`/auth/send-otp?email=${encodeURIComponent(cleanEmail)}`);
      } catch (e) {
        console.warn(e, "Auto send OTP on register warning");
      }

      toast.success(
        result.data?.message || 
        t("Register.success_registration_toast") || 
        "Registrasi sukses! Silakan verifikasi email Anda."
      );
      
      const targetSubmitOtpPath = locale === "en" 
        ? `/en/submit-otp?email=${encodeURIComponent(cleanEmail)}&name=${encodeURIComponent(cleanName)}&from=register` 
        : `/submit-otp?email=${encodeURIComponent(cleanEmail)}&name=${encodeURIComponent(cleanName)}&from=register`;

      router.push(targetSubmitOtpPath);
    } else {
      toast.error(result.error || t("Register.error_registration_failed_toast") || "Registrasi gagal");
    }
  };

  const handleGoogleRegister = async () => {
    setIsGoogleLoading(true);

    try {
      const result = await loginWithGoogle();

      if (result.success && result.url) {
        const googleUrl = new URL(result.url);
        
        if (!googleUrl.searchParams.has("prompt")) {
          googleUrl.searchParams.append("prompt", "select_account");
        }

        window.location.href = googleUrl.toString();
      } else {
        setIsGoogleLoading(false);
        toast.error(result.error || "Gagal mendapatkan tautan pendaftaran Google.");
      }
    } catch (error) {
      setIsGoogleLoading(false);
      toast.error("Gagal menghubungkan ke server Google.");
      console.error("Google register error:", error);
    }
  };

  const clearError = (field: keyof FormErrors) => {
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const isFormDisabled = isLoading || isGoogleLoading;

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-4 md:p-6 font-sans relative select-none">
      <div className="w-full max-w-[980px] flex justify-center lg:justify-between items-center lg:items-stretch gap-0 lg:gap-10">
        
        {/* BANNER KIRI */}
        <div className="hidden lg:flex w-[45%] bg-[#F15C61] rounded-[32px] p-8 pb-0 flex-col justify-between relative overflow-hidden min-h-[500px] shadow-[0_20px_50px_rgba(241,92,97,0.2)]">
          <div className="relative z-10 mt-1 pr-2 lg:pr-4">
            <h1 className="text-white text-[1.75rem] font-bold leading-[1.25] tracking-tight mb-3 text-balance">
              {t("Register.hero_title") || "Undangan Digital Profesional No.1 Di Indonesia. Untuk Segala Jenis Acara 100% GRATIS"}
            </h1>
            <p className="text-white/95 text-[0.9rem] leading-relaxed font-medium">
              {t("Register.hero_desc") || "Kelola undangan, RSVP, pengingat acara, hingga amplop digital dalam satu platform yang rapi, otomatis, dan gratis tanpa batas."}
            </p>
          </div>

          <div className="relative mt-5 flex-1 w-full flex justify-center translate-y-5">
            <div className="relative w-[300px] aspect-[3/4]">
              <div className="absolute left-1/2 top-[55%] -translate-x-1/2 -translate-y-1/2 w-[85%] h-[75%] bg-white/30 blur-[70px] rounded-full -z-10 pointer-events-none" />
              <Image 
                src="/images/cta/cta.png" 
                alt="Elynk App Mockup" 
                fill 
                className="object-contain object-bottom drop-shadow-xl relative z-10" 
                priority 
              />
            </div>
          </div>
        </div>

        {/* FORM KANAN */}
        <div className="w-full max-w-[440px] lg:w-[55%] lg:max-w-[420px] bg-white lg:bg-transparent shadow-sm lg:shadow-none rounded-3xl lg:rounded-none flex flex-col justify-center py-8 lg:py-5 px-6 lg:px-0 lg:pr-4">
          <h2 className="text-[1.5rem] lg:text-[1.65rem] font-bold text-[#2A2A2A] text-center mb-5 tracking-tight">
            {t("Register.create_account") || "Create Your Account"}
          </h2>

          <form onSubmit={handleSubmit} className="flex flex-col space-y-3.5" noValidate>
            
            {/* NAME INPUT */}
            <div className="flex flex-col space-y-1">
              <label className="text-[0.9rem] font-medium text-[#2A2A2A]">{t("Register.name_label") || "Your name"}</label>
              <div className="relative">
                <input 
                  type="text" 
                  disabled={isFormDisabled}
                  value={name}
                  onChange={(e) => { setName(e.target.value); clearError("name"); }}
                  placeholder={t("Register.name_placeholder") || "Jhon Doe"}
                  className={`w-full text-[#2A2A2A] placeholder-gray-400 rounded-[10px] h-[46px] px-4 outline-none transition-all text-[0.9rem]
                    ${formErrors.name 
                      ? 'bg-red-50 border border-red-400 focus:ring-2 focus:ring-red-400/30' 
                      : 'bg-[#F5F5F5] border-none focus:ring-2 focus:ring-[#F15C61]/30'}
                    ${isFormDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <UserIcon className={`absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] ${formErrors.name ? 'text-red-400' : 'text-gray-400'}`} strokeWidth={2} />
              </div>
              {formErrors.name && (
                <span className="flex items-center gap-1 text-red-500 text-[0.75rem] font-medium mt-1">
                  <ExclamationCircleIcon className="w-3.5 h-3.5" /> {formErrors.name}
                </span>
              )}
            </div>

            {/* EMAIL INPUT */}
            <div className="flex flex-col space-y-1">
              <label className="text-[0.9rem] font-medium text-[#2A2A2A]">{t("Register.email_label") || "Email address"}</label>
              <div className="relative">
                <input 
                  type="email" 
                  disabled={isFormDisabled}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearError("email"); }}
                  placeholder={t("Register.email_placeholder") || "username@domain.com"}
                  className={`w-full text-[#2A2A2A] placeholder-gray-400 rounded-[10px] h-[46px] px-4 outline-none transition-all text-[0.9rem]
                    ${formErrors.email 
                      ? 'bg-red-50 border border-red-400 focus:ring-2 focus:ring-red-400/30' 
                      : 'bg-[#F5F5F5] border-none focus:ring-2 focus:ring-[#F15C61]/30'}
                    ${isFormDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <AtSymbolIcon className={`absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] ${formErrors.email ? 'text-red-400' : 'text-gray-400'}`} strokeWidth={2} />
              </div>
              {formErrors.email && (
                <span className="flex items-center gap-1 text-red-500 text-[0.75rem] font-medium mt-1">
                  <ExclamationCircleIcon className="w-3.5 h-3.5" /> {formErrors.email}
                </span>
              )}
            </div>

            {/* PASSWORD INPUT */}
            <div className="flex flex-col space-y-1">
              <label className="text-[0.9rem] font-medium text-[#2A2A2A]">{t("Register.password_label") || "Make a password"}</label>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"} 
                  disabled={isFormDisabled}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearError("password"); }}
                  placeholder="Abc************"
                  className={`w-full text-[#2A2A2A] placeholder-gray-400 rounded-[10px] h-[46px] px-4 outline-none transition-all text-[0.9rem] pr-10
                    ${formErrors.password 
                      ? 'bg-red-50 border border-red-400 focus:ring-2 focus:ring-red-400/30' 
                      : 'bg-[#F5F5F5] border-none focus:ring-2 focus:ring-[#F15C61]/30'}
                    ${isFormDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3.5 top-1/2 -translate-y-1/2 outline-none transition-colors cursor-pointer
                    ${formErrors.password ? 'text-red-400 hover:text-red-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {showPassword ? <EyeIcon className="w-[18px] h-[18px]" strokeWidth={2} /> : <EyeSlashIcon className="w-[18px] h-[18px]" strokeWidth={2} />}
                </button>
              </div>
              {formErrors.password && (
                <span className="flex items-start gap-1 text-red-500 text-[0.75rem] font-medium mt-1 leading-tight">
                  <ExclamationCircleIcon className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span>{formErrors.password}</span>
                </span>
              )}
            </div>

            {/* CONFIRM PASSWORD INPUT */}
            <div className="flex flex-col space-y-1">
              <label className="text-[0.9rem] font-medium text-[#2A2A2A]">{t("Register.confirm_label") || "Confirmation Password"}</label>
              <div className="relative">
                <input 
                  type={showConfirmPassword ? "text" : "password"} 
                  disabled={isFormDisabled}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); clearError("confirmPassword"); }}
                  placeholder="Abc************"
                  className={`w-full text-[#2A2A2A] placeholder-gray-400 rounded-[10px] h-[46px] px-4 outline-none transition-all text-[0.9rem] pr-10
                    ${formErrors.confirmPassword 
                      ? 'bg-red-50 border border-red-400 focus:ring-2 focus:ring-red-400/30' 
                      : 'bg-[#F5F5F5] border-none focus:ring-2 focus:ring-[#F15C61]/30'}
                    ${isFormDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <button 
                  type="button" 
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={`absolute right-3.5 top-1/2 -translate-y-1/2 outline-none transition-colors cursor-pointer
                    ${formErrors.confirmPassword ? 'text-red-400 hover:text-red-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {showConfirmPassword ? <EyeIcon className="w-[18px] h-[18px]" strokeWidth={2} /> : <EyeSlashIcon className="w-[18px] h-[18px]" strokeWidth={2} />}
                </button>
              </div>
              {formErrors.confirmPassword && (
                <span className="flex items-center gap-1 text-red-500 text-[0.75rem] font-medium mt-1">
                  <ExclamationCircleIcon className="w-3.5 h-3.5" /> {formErrors.confirmPassword}
                </span>
              )}
            </div>

            {/* TERMS & CONDITIONS */}
            <div className="flex flex-col pt-1.5">
              <div className="flex items-center gap-2.5">
                <input 
                  type="checkbox" 
                  id="terms"
                  disabled={isFormDisabled}
                  checked={agreeTerms}
                  onChange={(e) => { setAgreeTerms(e.target.checked); clearError("terms"); }}
                  className={`w-4 h-4 rounded-[4px] border-gray-300 focus:ring-[#F15C61] bg-[#F5F5F5] cursor-pointer
                    ${formErrors.terms ? 'border-red-500 ring-1 ring-red-500 text-red-500' : 'text-[#F15C61]'}`}
                />
                <label htmlFor="terms" className={`text-[0.9rem] cursor-pointer select-none mt-0.5 ${formErrors.terms ? 'text-red-500' : 'text-gray-500'}`}>
                  {t("Register.agree_to") || "I agree to the"}{" "}
                  <Link href="/terms-of-service" target="_blank">
                    <span className={`font-bold ${formErrors.terms ? 'text-red-600' : 'text-[#2A2A2A]'}`}>
                      {t("Register.terms_conditions") || "Terms & Conditions"}
                    </span>
                  </Link>
                </label>
              </div>
              {formErrors.terms && (
                <span className="flex items-center gap-1 text-red-500 text-[0.75rem] font-medium mt-1.5">
                  <ExclamationCircleIcon className="w-3.5 h-3.5" /> {formErrors.terms}
                </span>
              )}
            </div>

            {/* SERVER ERROR ALERT */}
            {serverError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-[10px] text-[0.85rem] font-medium flex items-start gap-2 mt-2">
                 <ExclamationCircleIcon className="w-5 h-5 shrink-0" />
                 <span>{serverError}</span>
              </div>
            )}

            {/* SUBMIT BUTTON */}
            <div className="pt-2">
              <button 
                type="submit" 
                disabled={isFormDisabled}
                className="w-full flex items-center justify-center bg-[#F15C61] hover:bg-[#E04D52] disabled:bg-[#f15c61]/70 disabled:cursor-not-allowed text-white font-semibold text-[0.95rem] h-[46px] rounded-[10px] transition-colors shadow-sm cursor-pointer active:scale-[0.99]"
              >
                {isLoading ? (
                   <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                   </svg>
                ) : null}
                {isLoading ? (t("Register.creating_account") || "Creating Account...") : (t("Register.register_btn") || "Register")}
              </button>
            </div>

          </form>

          {/* DIVIDER */}
          <div className="flex items-center justify-center gap-3 my-4">
            <hr className="w-full border-gray-200" />
            <span className="text-[0.75rem] text-gray-400 whitespace-nowrap px-1">
              {t("Register.or_register_with") || "Or Register With"}
            </span>
            <hr className="w-full border-gray-200" />
          </div>

          {/* GOOGLE REGISTER BUTTON */}
          <button 
            type="button" 
            onClick={handleGoogleRegister}
            disabled={isFormDisabled}
            className="w-full flex items-center justify-center gap-2.5 h-[44px] text-[0.9rem] font-semibold text-[#2A2A2A] transition-colors bg-white border border-gray-200 rounded-[10px] hover:bg-gray-50 shadow-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed cursor-pointer active:scale-[0.99]"
          >
            {isGoogleLoading ? (
              <svg className="animate-spin h-4 w-4 text-[#2A2A2A]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.7511 10.1944C18.7511 9.47495 18.6915 8.94995 18.5626 8.40552H10.1797V11.6527H15.1003C15.0011 12.4597 14.4654 13.675 13.2749 14.4916L13.2582 14.6003L15.9087 16.6126L16.0924 16.6305C17.7788 15.1041 18.7511 12.8583 18.7511 10.1944Z" fill="#4285F4" />
                <path d="M10.1788 18.75C12.5895 18.75 14.6133 17.9722 16.0915 16.6305L13.274 14.4916C12.5201 15.0068 11.5081 15.3666 10.1788 15.3666C7.81773 15.3666 5.81379 13.8402 5.09944 11.7305L4.99473 11.7392L2.23868 13.8295L2.20264 13.9277C3.67087 16.786 6.68674 18.75 10.1788 18.75Z" fill="#34A853" />
                <path d="M5.10014 11.7305C4.91165 11.186 4.80257 10.6027 4.80257 9.99992C4.80257 9.3971 4.91165 8.81379 5.09022 8.26935L5.08523 8.1534L2.29464 6.02954L2.20333 6.0721C1.5982 7.25823 1.25098 8.5902 1.25098 9.99992C1.25098 11.4096 1.5982 12.7415 2.20333 13.9277L5.10014 11.7305Z" fill="#FBBC05" />
                <path d="M10.1789 4.63331C11.8554 4.63331 12.9864 5.34303 13.6312 5.93612L16.1511 3.525C14.6035 2.11528 12.5895 1.25 10.1789 1.25C6.68676 1.25 3.67088 3.21387 2.20264 6.07218L5.08953 8.26943C5.81381 6.15972 7.81776 4.63331 10.1789 4.63331Z" fill="#EB4335" />
              </svg>
            )}
            {isGoogleLoading ? (t("Register.redirecting") || "Mengarahkan...") : (t("Register.google_btn") || "Daftar dengan Google")}
          </button>

          <div className="mt-5 text-center text-[0.9rem] text-[#2A2A2A]">
            {t("Register.already_have_account") || "Already have an account?"}{" "}
            <Link href="/login" className="font-bold underline hover:text-[#F15C61] transition-colors">
              {t("Register.login_link") || "Login"}
            </Link>
          </div>

        </div>

      </div>
    </div>
  );
}